/**
 * The six behaviour families (docs/05 §3), as small state machines over the ECS
 * arrays. No per-entity objects: every function takes the shared `AiFrame` and
 * an entity index, and all persistent state lives in `aiState`, `aiTimer`,
 * `aiTarget`, `aiHome` and the four `aiBlackboard` scratch floats.
 *
 * **Shapers are the identity family.** Chargers, zoners, swarms, anchors and
 * parasites are competent versions of things every action game has. A shaper
 * floods the room you are standing in, sets the oil you were saving on fire, or
 * walls off the corridor you were backing toward — it attacks by editing the
 * simulation. That is the difference between a material sandbox and a material
 * *threat*, so the terrain ops below get the most care and the tightest budget
 * accounting in this file.
 *
 * Blackboard convention (4 floats, per entity):
 *   [0] attack cooldown remaining, seconds
 *   [1] selected attack index (-1 = none)
 *   [2] family scratch — strafe direction, panic level, attach handle
 *   [3] pulses remaining for a multi-pulse terrain op
 */

import {
  Comp,
  NULL_ENTITY,
  Team,
  World,
  type Entity,
} from '../ecs';
import {
  Anchor,
  AttackKind,
  Family,
  ShaperOp,
  Status,
  digPower,
  type AttackDef,
  type EnemyDef,
  type EnemyRegistry,
  type StatusId,
} from '../enemyData';
import {
  applyStatus, hasStatus, removeStatus as clearStatus, statusStacks,
} from '../../combat/status';
import { Flag, M, VOID, type MatId } from '../../sim/materials';
import { MAT, type MaterialName } from '../../sim/materialData';
import type { CellWorld } from '../../sim/world';
import type { Rng } from '../../core/rng';
import { SUSTAINED_IFRAMES } from './enemyAttacks';
import {
  ProbeBudget,
  canDigThrough,
  ceilingAbove,
  flyStep,
  groundBelow,
  groundFollow,
  swimStep,
  type NavCaps,
} from './pathing';

// ------------------------------------------------------------------ states

export const AiState = {
  IDLE: 0,
  ALERT: 1,
  APPROACH: 2,
  COMMIT: 3,
  RECOVER: 4,
  FLEE: 5,
  RETURN: 6,
} as const;
export type AiStateId = (typeof AiState)[keyof typeof AiState];
export const AI_STATE_NAMES = ['idle', 'alert', 'approach', 'commit', 'recover', 'flee', 'return'];

const BB_COOLDOWN = 0;
const BB_ATTACK = 1;
const BB_SCRATCH = 2;
const BB_PULSE = 3;

// ------------------------------------------------------------------ context

/**
 * Everything the AI needs from systems it does not own. All the combat hooks
 * are optional: the AI still runs (and still shapes terrain) in a world where
 * the combat workstream has not landed yet, which is what lets these two
 * develop in parallel.
 */
export interface EnemyAiCtx {
  registry: EnemyRegistry;

  /** Fire a projectile. `dirX/dirY` are normalised. */
  spawnProjectile?(
    srcIdx: number,
    x: number,
    y: number,
    dirX: number,
    dirY: number,
    atk: AttackDef,
    def: EnemyDef,
  ): void;
  /** Resolve a melee/beam/aura hit, where the attacker stands. */
  meleeHit?(srcIdx: number, atk: AttackDef, def: EnemyDef, dirX: number, dirY: number): void;
  /**
   * Resolve a blow over the ground the body covered since `(fromX, fromY)`.
   *
   * A lunge's damage travels with the dash rather than being claimed from the
   * launch point in one frame (#49), so the attack is resolved once per tick of
   * its active window over the segment just crossed.
   */
  sweptHit?(
    srcIdx: number,
    atk: AttackDef,
    def: EnemyDef,
    dirX: number,
    dirY: number,
    fromX: number,
    fromY: number,
  ): void;
  /**
   * Resolve a terrain op's damage at `(atX, atY)` — the anchor its cells were
   * written at, not the caster (#46). See `terrainHit` in `enemyAttacks.ts` for
   * why the op's footprint owns the damage and the caster does not.
   */
  terrainHit?(
    srcIdx: number,
    atk: AttackDef,
    def: EnemyDef,
    dirX: number,
    dirY: number,
    atX: number,
    atY: number,
  ): void;
  /**
   * Resolve the body's own `contactDamage` against whatever it is overlapping.
   * Called once per stepped enemy per tick by `updateEnemies`, not from a
   * behaviour: standing inside an enemy costs you HP whatever it is doing.
   */
  contactHit?(srcIdx: number, def: EnemyDef): void;
  /**
   * An enemy has entered the windup of an attack lasting `durationSec`.
   *
   * The windup is the tell (see `driveAttack`), and a tell nothing is told
   * about is a comment. This travels through the context rather than through a
   * module-level registration so that the announcement is part of the AI's
   * contract with whatever is drawing it, and so a headless caller that
   * supplies no hook simply does not pose.
   */
  noteAttack?(idx: number, durationSec: number): void;
  /**
   * Apply damage to an entity index — used for environmental damage too.
   *
   * `srcMat` is the material that did it, when a material did it. It is
   * optional so existing four-argument handlers stay assignable, but supplying
   * it is what lets the death readout say "killed by lava" instead of
   * "killed by the world" (docs/06 §4).
   */
  damageEntity?(idx: number, amount: number, type: number, srcIdx: number, srcMat?: MatId): void;
  /** Spawn a minion; returns the new entity index or -1. */
  spawnEnemy?(id: string, x: number, y: number, parentIdx: number): number;
  /** Called once when an enemy dies, after its corpse has been written. */
  onDeath?(idx: number, def: EnemyDef): void;
  /** Telemetry hook: how many cells a shaper pulse actually wrote. */
  onShaperPulse?(idx: number, atk: AttackDef, cellsWritten: number): void;

  // --- budgets
  /** Enemies stepped per tick before round-robin kicks in. */
  maxEnemiesPerTick?: number;
  /** Cell writes all shapers share per tick. Keeps a boss fight affordable. */
  maxCellWritesPerTick?: number;
  /** Cell reads each enemy may spend on navigation per tick. */
  maxProbesPerEntity?: number;
  /** Set false to leave entity destruction to the combat workstream. */
  destroyOnDeath?: boolean;
}

/** Reused every entity, every tick. Never allocated in the loop. */
export interface AiFrame {
  w: World;
  cells: CellWorld;
  rng: Rng;
  dt: number;
  /** Seconds since world start — for cooldowns that outlive a state. */
  now: number;

  i: number;
  def: EnemyDef;
  caps: NavCaps;

  /** Player index, or -1. */
  target: number;
  tx: number;
  ty: number;
  dx: number;
  dy: number;
  dist: number;
  los: boolean;

  probes: ProbeBudget;
  /** Shared across all shapers this tick; decremented by terrain ops. */
  cellBudget: CellBudget;
  ctx: EnemyAiCtx;
}

export interface CellBudget {
  left: number;
}

// ------------------------------------------------------------ status helpers

/**
 * Status slots are a fixed 8 per entity. Re-applying an active status refreshes
 * it and stacks intensity rather than consuming a second slot, so a burning
 * enemy that walks through more fire burns harder, not twice.
 *
 * This is a thin forward to `combat/status.ts`, which owns the tuning table,
 * the opposition rules and the per-status cell coupling. There used to be a
 * second, simpler implementation here; two writers over one array is how the
 * two status numberings survived as long as they did, and the local copy also
 * never set `Comp.STATUS`, so anything it applied was invisible to the tick
 * that damages over time.
 */
export { applyStatus, hasStatus, statusStacks, clearStatus };

// ------------------------------------------------------------ small helpers

function bb(f: AiFrame, slot: number): number {
  return f.w.aiBlackboard[f.i * 4 + slot];
}
function setBb(f: AiFrame, slot: number, v: number): void {
  f.w.aiBlackboard[f.i * 4 + slot] = v;
}

function enter(f: AiFrame, state: AiStateId): void {
  f.w.aiState[f.i] = state;
  f.w.aiTimer[f.i] = 0;
}

function face(f: AiFrame, dirX: number): void {
  if (dirX > 0.05) f.w.facing[f.i] = 1;
  else if (dirX < -0.05) f.w.facing[f.i] = -1;
}

/** Horizontal drive toward a speed, respecting the def's acceleration. */
function drive(f: AiFrame, dirX: number, speedScale = 1): void {
  const { w, i, def } = f;
  const want = dirX * def.speed * speedScale;
  const step = def.accel * f.dt;
  const v = w.vx[i];
  w.vx[i] = v < want ? Math.min(v + step, want) : Math.max(v - step, want);
  face(f, dirX);
}

function driveY(f: AiFrame, dirY: number, speedScale = 1): void {
  const { w, i, def } = f;
  const want = dirY * def.speed * speedScale;
  const step = def.accel * f.dt;
  const v = w.vy[i];
  w.vy[i] = v < want ? Math.min(v + step, want) : Math.max(v - step, want);
}

function isFlyer(def: EnemyDef): boolean {
  // BodyFlag.NO_GRAVITY === 1<<0, FLOATS === 1<<3. Imported numerically to keep
  // this a single mask test in the hot path.
  return (def.bodyFlags & 0b1001) !== 0;
}

/**
 * One step of locomotion toward a point, choosing the right navigator for the
 * body. Walkers get ground-follow with ledge/gap handling; fliers and swimmers
 * get the steering variants.
 */
function navigateTo(f: AiFrame, x: number, y: number, speedScale = 1): void {
  const { w, i, def } = f;
  if (isFlyer(def)) {
    const submerged = w.submersion[i] > 0.5;
    const nav = submerged
      ? swimStep(f.cells, w.x[i], w.y[i], x, y, bodyDensityCache(def), f.caps, f.probes)
      : flyStep(f.cells, w.x[i], w.y[i], x, y, f.caps, f.probes);
    drive(f, nav.moveX, speedScale);
    driveY(f, nav.moveY, speedScale);
    if (nav.dig) tryDig(f, nav.digX, nav.digY);
    return;
  }

  const nav = groundFollow(f.cells, w.x[i], w.y[i], x, y, f.caps, f.probes);
  drive(f, nav.moveX, speedScale);
  if (nav.jump && w.grounded[i] === 1) w.vy[i] = -nav.jumpSpeed;
  if (nav.dig) tryDig(f, nav.digX, nav.digY);
  if (nav.blocked) {
    // Nowhere to go this way. Burrowers already tried digging; everyone else
    // stops rather than grinding into the wall, which reads as intent.
    w.vx[i] *= 0.5;
  }
}

const densityCache = new Map<string, number>();
function bodyDensityCache(def: EnemyDef): number {
  let d = densityCache.get(def.id);
  if (d === undefined) {
    let acc = 0;
    let tot = 0;
    for (const c of def.composition) {
      acc += c.fraction * M.density[MAT[c.mat]];
      tot += c.fraction;
    }
    d = tot > 0 ? acc / tot : 1000;
    densityCache.set(def.id, d);
  }
  return d;
}

const digCache = new Map<string, number>();
function digPowerCached(def: EnemyDef): number {
  let p = digCache.get(def.id);
  if (p === undefined) {
    p = digPower(def);
    digCache.set(def.id, p);
  }
  return p;
}

function tryDig(f: AiFrame, x: number, y: number): void {
  if (f.cellBudget.left <= 0) return;
  const power = digPowerCached(f.def);
  if (!canDigThrough(f.cells, x, y, power, f.probes)) return;
  // Chew a small bite, not a tunnel: the enemy should be visibly *working*.
  for (let oy = -1; oy <= 1 && f.cellBudget.left > 0; oy++) {
    for (let ox = -1; ox <= 1 && f.cellBudget.left > 0; ox++) {
      if (f.cells.damageCell(x + ox, y + oy, power)) f.cellBudget.left--;
    }
  }
}

// ------------------------------------------------------------ attack driving

function cooldownReady(f: AiFrame): boolean {
  return bb(f, BB_COOLDOWN) <= 0;
}

/**
 * Pick the highest-value attack that is in range. Terrain ops are biased *up*
 * when the player is at range, because a shaper standing still poking at you is
 * a worse enemy than one that changes the room.
 */
function pickAttack(f: AiFrame): number {
  const { def } = f;
  let best = -1;
  let bestScore = -1;
  for (let a = 0; a < def.attacks.length; a++) {
    const atk = def.attacks[a];
    if (f.dist > atk.range) continue;
    if (atk.kind !== AttackKind.TERRAIN && !f.los) continue;
    let score = atk.damage + atk.range * 0.05;
    if (atk.kind === AttackKind.TERRAIN) score += 24 + atk.cells * 0.15;
    if (atk.kind === AttackKind.SUMMON) score += 18;
    score *= 0.75 + f.rng.float() * 0.5; // avoid lockstep across a pack
    if (score > bestScore) {
      bestScore = score;
      best = a;
    }
  }
  return best;
}

function beginAttack(f: AiFrame, index: number): void {
  setBb(f, BB_ATTACK, index);
  setBb(f, BB_PULSE, attackPulses(f.def.attacks[index]));
  enter(f, AiState.COMMIT);
}

/**
 * How many times an attack lands inside its own active window.
 *
 * Authored `pulses` is a *shaper* number: it spreads a flood's 200 cell writes
 * across the window instead of dumping them in one frame, and the thirteen
 * multi-pulse attacks in the roster are all terrain ops. For a **sustained**
 * hit — an aura or a beam — the answer is not authorable at all: it is however
 * many times the re-hit gate opens while the attack is up, and the gate is
 * `SUSTAINED_IFRAMES`. All four beams are authored `pulses: 1`, which is why a
 * 0.5 s beam landed once and `SUSTAINED_IFRAMES` was unreachable code.
 *
 * Derived rather than authored on eight defs, because the derivation cannot
 * disagree with itself: lengthen `active` and the beam ticks more, with no
 * second field to forget.
 */
export function attackPulses(atk: AttackDef): number {
  if (atk.kind !== AttackKind.AURA && atk.kind !== AttackKind.BEAM) return atk.pulses;
  return Math.max(atk.pulses, Math.max(1, Math.round(atk.active / SUSTAINED_IFRAMES)));
}

/**
 * Runs the windup → active → recover envelope. Returns true while the attack is
 * still going. The windup is the tell: it is deliberately long enough to read,
 * because docs/05 §7 asks that every death be traceable to a cause.
 */
function driveAttack(f: AiFrame): boolean {
  const { w, i, def } = f;
  const index = bb(f, BB_ATTACK) | 0;
  if (index < 0 || index >= def.attacks.length) return false;
  const atk = def.attacks[index];
  const t = w.aiTimer[i];
  const prev = t - f.dt;
  const pulses = attackPulses(atk);

  // The first tick of the envelope, whoever put us here. Seeding the pulse
  // counter and announcing both belong at the *start of the commit*, not
  // inside `beginAttack`: a family that drops an entity into COMMIT by another
  // route (and the test rig that does the same) must get the same tell and the
  // same schedule, or the announcement is a claim about one code path.
  // Half a tick, not zero: `aiTimer` is a Float32Array and `dt` is a float64,
  // so `t - dt` on the first tick is 4e-10 rather than 0. A test for "is this
  // the opening tick" has to be robust to that or the tell never fires.
  if (prev < f.dt * 0.5) {
    setBb(f, BB_PULSE, pulses);
    f.ctx.noteAttack?.(i, atk.windup + atk.active);
  }

  // Face the target through the windup; commit to that facing afterwards.
  if (t < atk.windup && f.target >= 0) face(f, f.dx);

  const activeStart = atk.windup;
  const activeEnd = atk.windup + atk.active;

  if (prev < activeStart && t >= activeStart) {
    fireAttack(f, atk);
  } else if (t >= activeStart && t < activeEnd && atk.kind === AttackKind.LUNGE) {
    // The dash *is* the attack, so it keeps resolving while the body travels
    // (#49). Resolved once at launch it had to claim the whole swept path in
    // one frame — an instantaneous 136-unit box that still landed on nobody,
    // because the trigger range was further out than the path — and it could
    // never hit anything that stepped in front of it mid-dash.
    sweepLunge(f, atk);
  } else if (t >= activeStart && t < activeEnd && pulses > 1) {
    // Multi-pulse attacks spread their landings across the active window: a
    // flood keeps pouring instead of dumping every cell in one frame, and a
    // beam keeps burning instead of being a very slow melee swing.
    //
    // A sustained attack is spaced at its own re-hit gate — anything tighter
    // schedules a pulse that `w.iframes` then eats, which is a pulse that
    // costs a hitbox scan and does nothing.
    const sustained = atk.kind === AttackKind.AURA || atk.kind === AttackKind.BEAM;
    const per = sustained ? SUSTAINED_IFRAMES : atk.active / pulses;
    // `done` counts pulses already fired, so pulse k is due at k × per. It used
    // to be due at (k+1) × per, which put the last pulse of every op at exactly
    // `activeEnd` — outside the window that fires it. Every multi-pulse attack
    // in the roster was quietly delivering one pulse fewer than it authored.
    const done = pulses - bb(f, BB_PULSE);
    // A sustained pulse lands on the first tick *at or after* it is due, so the
    // iframe window it opens closes up to one tick later than the schedule
    // assumes. Scheduling on the bare multiple races that window by fractions
    // of a tick and the pulse is silently eaten — which is one landed hit per
    // beam, i.e. exactly the defect the derivation above exists to fix. One
    // tick of slack is the largest that slip can be, so this cannot race.
    const slack = sustained ? f.dt : 0;
    if (t - activeStart >= done * per + slack && bb(f, BB_PULSE) > 0) {
      fireAttack(f, atk);
    }
  }

  if (t >= activeEnd + atk.recover) {
    setBb(f, BB_COOLDOWN, atk.cooldown);
    setBb(f, BB_ATTACK, -1);
    return false;
  }
  return true;
}

/**
 * One tick of a lunge in flight: the blow, resolved over the ground the body
 * has just crossed.
 *
 * The segment is `[x - v·dt, x]` — behind the body, never ahead of it. A dash
 * may not claim ground it has not reached yet; that is the whole defect this
 * replaces. On the launch tick the body has not moved, so `fireAttack`'s own
 * `meleeHit` covers it and this takes over from the next one.
 *
 * Aim comes from the velocity rather than from the target: a charger does not
 * steer once it commits (see `charger`), so the direction the damage and its
 * knockback point is the direction the body is actually going. Facing is the
 * fallback for the frame a dash has been brought to a stop.
 *
 * It does not need a "have I hit yet" flag. `w.iframes` is already a single
 * per-target window of `ENEMY_IFRAMES` (0.35 s) and every authored lunge window
 * is shorter than that, so no body can be hit twice by one dash — while a dash
 * *through* a pack still hits each body it passes over once, which is what a
 * charge should do. `tests/enemyCombat.test.ts` asserts the one-hit half, so
 * authoring an `active` past the iframe window goes red rather than silently
 * multiplying a charger's damage by the tick rate.
 */
function sweepLunge(f: AiFrame, atk: AttackDef): void {
  const { w, i, ctx, def } = f;
  if (ctx.sweptHit === undefined || atk.damage <= 0) return;

  const vx = w.vx[i];
  const vy = w.vy[i];
  const speed = Math.hypot(vx, vy);
  const nx = speed > 1e-3 ? vx / speed : w.facing[i];
  const ny = speed > 1e-3 ? vy / speed : 0;

  ctx.sweptHit(i, atk, def, nx, ny, w.x[i] - vx * f.dt, w.y[i] - vy * f.dt);
}

function fireAttack(f: AiFrame, atk: AttackDef): void {
  const { w, i, ctx, def } = f;
  setBb(f, BB_PULSE, bb(f, BB_PULSE) - 1);

  const len = f.dist > 1e-4 ? f.dist : 1;
  const nx = f.dx / len;
  const ny = f.dy / len;

  switch (atk.kind) {
    case AttackKind.TERRAIN: {
      const written = executeShaperOp(f, atk);
      ctx.onShaperPulse?.(i, atk, written);
      // Terrain ops that carry damage apply it as an area effect at the anchor
      // — the place the op just wrote its cells, which `executeShaperOp` has
      // left in `opX`/`opY`. It used to ask for the damage at the caster, so a
      // ceiling-anchored flood wrote lava overhead and hurt whatever was
      // standing next to the shaper, 200 units away (#46).
      if (atk.damage > 0) ctx.terrainHit?.(i, atk, def, nx, ny, opX, opY);
      break;
    }
    case AttackKind.PROJECTILE: {
      const n = atk.count > 0 ? atk.count : 1;
      for (let k = 0; k < n; k++) {
        // A fan, not a line — a stack of identical projectiles is unreadable.
        const spread = n === 1 ? 0 : (k / (n - 1) - 0.5) * 0.4;
        const c = Math.cos(spread);
        const s = Math.sin(spread);
        ctx.spawnProjectile?.(i, w.x[i], w.y[i], nx * c - ny * s, nx * s + ny * c, atk, def);
      }
      break;
    }
    case AttackKind.SUMMON: {
      if (ctx.spawnEnemy && atk.summon) {
        for (let k = 0; k < atk.count; k++) {
          const ox = f.rng.range(-40, 40);
          ctx.spawnEnemy(atk.summon, w.x[i] + ox, w.y[i] - 8, i);
        }
      }
      break;
    }
    case AttackKind.LUNGE: {
      w.vx[i] = nx * atk.speed;
      if (!isFlyer(def)) w.vy[i] = Math.min(w.vy[i], -80);
      else w.vy[i] = ny * atk.speed;
      ctx.meleeHit?.(i, atk, def, nx, ny);
      break;
    }
    case AttackKind.ATTACH: {
      const host = bb(f, BB_SCRATCH) | 0;
      if (host > 0) applyStatus(w, host - 1, Status.BUFFED, 8, 1);
      ctx.meleeHit?.(i, atk, def, nx, ny);
      break;
    }
    default:
      ctx.meleeHit?.(i, atk, def, nx, ny);
      break;
  }
}

// ==========================================================================
// SHAPER TERRAIN OPS — the identity mechanic
// ==========================================================================

/**
 * Op centre, resolved per anchor. Written into module-level scalars because
 * this runs inside the per-enemy loop and a returned {x,y} would be an
 * allocation per pulse.
 */
let opX = 0;
let opY = 0;

function resolveAnchor(f: AiFrame, atk: AttackDef): void {
  const { w, i } = f;
  switch (atk.anchor) {
    case Anchor.SELF:
      opX = w.x[i];
      opY = w.y[i];
      break;
    case Anchor.TARGET:
      // Biased toward the target's feet, not its centre. Freezing, venting and
      // igniting all want to land on the ground someone is standing on; aimed
      // at a torso they miss the floor by half a body height and do nothing.
      opX = f.tx;
      opY = f.ty + (f.target >= 0 ? w.halfH[f.target] * 0.6 : 0);
      break;
    case Anchor.CEILING: {
      // Pour from the roof above the target so the liquid falls *onto* them and
      // the sim does the rest. If the room is open, use a fixed height instead.
      const up = ceilingAbove(f.cells, Math.floor(f.tx), Math.floor(f.ty), 90, f.probes);
      opX = f.tx;
      opY = up > 0 ? f.ty - up + 1 : f.ty - 40;
      break;
    }
    case Anchor.FLOOR: {
      const down = groundBelow(f.cells, Math.floor(f.tx), Math.floor(f.ty), 60, f.probes);
      opX = f.tx;
      opY = down > 0 ? f.ty + down - 1 : f.ty + 8;
      break;
    }
    case Anchor.BETWEEN:
    default:
      opX = (w.x[i] + f.tx) * 0.5;
      opY = (w.y[i] + f.ty) * 0.5;
      break;
  }
}

/**
 * Execute one pulse of a terrain attack. Returns the number of cells written.
 *
 * Every op is bounded by three things at once: the attack's own `cells` figure,
 * the shared per-tick cell budget, and the geometry it is allowed to touch.
 * That triple bound is what makes "the boss floods the arena" a 60 fps feature
 * rather than a stutter.
 */
export function executeShaperOp(f: AiFrame, atk: AttackDef): number {
  // Before the budget check, not after: the anchor is where the op *is*, and
  // `fireAttack` reads it to place the damage (#46). An op that wrote nothing
  // still happened — an IGNITE in a wet room writes zero cells by design — so
  // the two must not be able to disagree about whether there was a pulse.
  resolveAnchor(f, atk);
  if (f.cellBudget.left <= 0) return 0;
  const cap = Math.min(atk.cells, f.cellBudget.left);
  if (cap <= 0) return 0;

  let written = 0;
  switch (atk.op) {
    case ShaperOp.FLOOD:
      written = opFlood(f, atk, cap);
      break;
    case ShaperOp.WALL:
      written = opWall(f, atk, cap);
      break;
    case ShaperOp.VENT:
      written = opVent(f, atk, cap);
      break;
    case ShaperOp.IGNITE:
      written = opIgnite(f, atk, cap);
      break;
    case ShaperOp.FREEZE:
      written = opFreeze(f, atk, cap);
      break;
    case ShaperOp.ERODE:
      written = opErode(f, atk, cap);
      break;
    case ShaperOp.SEED:
      written = opSeed(f, atk, cap);
      break;
    case ShaperOp.ERASE:
      written = opErase(f, atk, cap);
      break;
    case ShaperOp.CHARGE:
      written = opCharge(f, atk, cap);
      break;
    default:
      break;
  }
  f.cellBudget.left -= written;
  return written;
}

function matOf(name: MaterialName | null): MatId {
  return name === null ? VOID : MAT[name];
}

/** True if a cell can be overwritten by a fluid/gas the shaper is emitting. */
function displaceable(cells: CellWorld, x: number, y: number, density: number): boolean {
  const m = cells.peek(x, y);
  if (m === VOID) return true;
  if (M.solid[m] === 1) return false;
  return density >= M.density[m];
}

/**
 * FLOOD — pour a liquid into the room. Anchored at the ceiling this is a
 * genuine "the room is filling up" threat; anchored at the floor it lays a
 * puddle that everything downstream (fire, charge, freezing) can act on.
 */
function opFlood(f: AiFrame, atk: AttackDef, cap: number): number {
  const mat = matOf(atk.mat);
  if (mat === VOID) return 0;
  const density = M.density[mat];
  const r = atk.radius;
  let n = 0;
  // Emit along a horizontal band at the anchor: liquids find their own level,
  // so a wide thin source produces a far more convincing pour than a disc.
  for (let pass = 0; pass < 3 && n < cap; pass++) {
    for (let ox = -r; ox <= r && n < cap; ox++) {
      const x = Math.floor(opX + ox);
      const y = Math.floor(opY + pass);
      if (!displaceable(f.cells, x, y, density)) continue;
      // Jitter so the source edge is ragged rather than a suspicious straight line.
      if (f.rng.float() > 0.75) continue;
      f.cells.set(x, y, mat);
      n++;
    }
  }
  return n;
}

/**
 * WALL — build a barrier. Aimed between the shaper and its target, so its
 * tactical meaning is "you may not stand there any more". It is ordinary
 * terrain: breakable, burnable, and made of whatever the shaper is made of.
 */
function opWall(f: AiFrame, atk: AttackDef, cap: number): number {
  const mat = matOf(atk.mat);
  if (mat === VOID) return 0;
  const height = atk.radius;
  const thickness = f.def.rank === 0 ? 2 : 3;
  const baseX = Math.floor(opX);
  const floor = groundBelow(f.cells, baseX, Math.floor(opY), 48, f.probes);
  const footY = floor >= 0 ? Math.floor(opY) + floor - 1 : Math.floor(opY);
  let n = 0;
  for (let t = 0; t < thickness && n < cap; t++) {
    const x = baseX + t - (thickness >> 1);
    for (let h = 0; h < height && n < cap; h++) {
      const y = footY - h;
      const m = f.cells.peek(x, y);
      if (m !== VOID && M.solid[m] === 1) continue;
      f.cells.set(x, y, mat);
      n++;
    }
  }
  return n;
}

/**
 * VENT — emit gas. The gas then does what gas does: chlorine sinks into the
 * trench you were using for cover, methane pools at the ceiling, spores wait
 * for someone to bring a torch. The shaper does not model any of that.
 */
function opVent(f: AiFrame, atk: AttackDef, cap: number): number {
  const mat = matOf(atk.mat);
  if (mat === VOID) return 0;
  const density = M.density[mat];
  const r = atk.radius;
  let n = 0;
  const attempts = Math.min(cap * 3, 400);
  for (let k = 0; k < attempts && n < cap; k++) {
    const a = f.rng.float() * Math.PI * 2;
    const d = Math.sqrt(f.rng.float()) * r;
    const x = Math.floor(opX + Math.cos(a) * d);
    const y = Math.floor(opY + Math.sin(a) * d);
    if (!displaceable(f.cells, x, y, density)) continue;
    f.cells.set(x, y, mat);
    n++;
  }
  return n;
}

/**
 * IGNITE — the single best argument for the whole family. It creates nothing:
 * it raises the temperature of cells that are *already* flammable past their
 * ignition point. In a wet room it does nothing. In a room you filled with oil
 * or spore cloud, it is the end of the fight.
 */
function opIgnite(f: AiFrame, atk: AttackDef, cap: number): number {
  const r = atk.radius;
  let n = 0;
  for (let oy = -r; oy <= r && n < cap; oy++) {
    for (let ox = -r; ox <= r && n < cap; ox++) {
      if (ox * ox + oy * oy > r * r) continue;
      const x = Math.floor(opX + ox);
      const y = Math.floor(opY + oy);
      const m = f.cells.peek(x, y);
      if (m === VOID) continue;
      const ign = M.ignitionTemp[m];
      if (ign >= 32000) continue;
      f.cells.setTemp(x, y, ign + 60);
      f.cells.addFlag(x, y, Flag.BURNING);
      n++;
    }
  }
  return n;
}

/**
 * FREEZE — chill a region. Liquids become their frozen form (water → ice,
 * mud → permafrost), and everything else just gets cold, which matters because
 * §5.1 makes frozen organics brittle. Freeze-then-shatter is a player combo;
 * this is the enemy using it on the room.
 */
function opFreeze(f: AiFrame, atk: AttackDef, cap: number): number {
  const r = atk.radius;
  let n = 0;
  for (let oy = -r; oy <= r && n < cap; oy++) {
    for (let ox = -r; ox <= r && n < cap; ox++) {
      if (ox * ox + oy * oy > r * r) continue;
      const x = Math.floor(opX + ox);
      const y = Math.floor(opY + oy);
      const m = f.cells.peek(x, y);
      if (m === VOID) continue;
      const target = M.freezesAt[m];
      if (target > -273) {
        f.cells.setTemp(x, y, target - 20);
        const into = M.freezesInto[m];
        if (into >= 0) f.cells.transmute(x, y, into);
        f.cells.addFlag(x, y, Flag.FROZEN);
      } else {
        f.cells.addTemp(x, y, -140);
      }
      n++;
    }
  }
  return n;
}

/**
 * ERODE — dig. Uses the same `hardness` gate as everything else that destroys
 * terrain, so an anchor that "collapses the floor" cannot cheat through granite
 * that your Breach module also cannot cheat through.
 */
function opErode(f: AiFrame, atk: AttackDef, cap: number): number {
  const power = Math.max(2, digPowerCached(f.def));
  const r = atk.radius;
  let n = 0;
  for (let oy = -r; oy <= r && n < cap; oy++) {
    for (let ox = -r; ox <= r && n < cap; ox++) {
      if (ox * ox + oy * oy > r * r) continue;
      if (f.cells.damageCell(Math.floor(opX + ox), Math.floor(opY + oy), power)) n++;
    }
  }
  return n;
}

/**
 * SEED — grow living matter onto exposed surfaces. Unlike FLOOD it only writes
 * where there is something to grow *on*, which is why a fungal bloom follows
 * the shape of the room and a flood does not.
 */
function opSeed(f: AiFrame, atk: AttackDef, cap: number): number {
  const mat = matOf(atk.mat);
  if (mat === VOID) return 0;
  const r = atk.radius;
  let n = 0;
  const attempts = Math.min(cap * 4, 600);
  for (let k = 0; k < attempts && n < cap; k++) {
    const a = f.rng.float() * Math.PI * 2;
    const d = Math.sqrt(f.rng.float()) * r;
    const x = Math.floor(opX + Math.cos(a) * d);
    const y = Math.floor(opY + Math.sin(a) * d);
    if (f.cells.peek(x, y) !== VOID) continue;
    // Only where it has purchase: a solid neighbour in any of the four dirs.
    const anchored =
      f.cells.isSolid(x, y + 1) ||
      f.cells.isSolid(x, y - 1) ||
      f.cells.isSolid(x - 1, y) ||
      f.cells.isSolid(x + 1, y);
    if (!anchored) continue;
    f.cells.set(x, y, mat);
    n++;
  }
  return n;
}

/**
 * ERASE — void unmaking. Respects `indestructible`, because the adamant seals
 * that gate progression must not be removable by a wandering pilgrim.
 */
function opErase(f: AiFrame, atk: AttackDef, cap: number): number {
  const r = atk.radius;
  let n = 0;
  const voidMat = MAT.VOID_MATTER;
  for (let oy = -r; oy <= r && n < cap; oy++) {
    for (let ox = -r; ox <= r && n < cap; ox++) {
      if (ox * ox + oy * oy > r * r) continue;
      const x = Math.floor(opX + ox);
      const y = Math.floor(opY + oy);
      const m = f.cells.peek(x, y);
      if (m === VOID || M.indestructible[m] === 1) continue;
      // §5.1: void erases, with a chance of leaving void matter behind.
      if (f.rng.chance(0.12)) f.cells.set(x, y, voidMat);
      else f.cells.set(x, y, VOID);
      n++;
    }
  }
  return n;
}

/**
 * CHARGE — electrify conductive cells. On its own it is nearly nothing; after a
 * mercury or brine pour it is the whole floor. The two-attack combination is
 * the mercury wisp's entire identity and it is expressed in eight lines here
 * because the charge/wet interaction already exists in the sim.
 */
function opCharge(f: AiFrame, atk: AttackDef, cap: number): number {
  const r = atk.radius;
  let n = 0;
  for (let oy = -r; oy <= r && n < cap; oy++) {
    for (let ox = -r; ox <= r && n < cap; ox++) {
      if (ox * ox + oy * oy > r * r) continue;
      const x = Math.floor(opX + ox);
      const y = Math.floor(opY + oy);
      const m = f.cells.peek(x, y);
      if (m === VOID) continue;
      const flags = f.cells.getFlags(x, y);
      const conducts = M.electricConductivity[m] > 0.2 || (flags & Flag.WET) !== 0;
      if (!conducts) continue;
      f.cells.addFlag(x, y, Flag.CHARGED);
      n++;
    }
  }
  return n;
}

// ==========================================================================
// Behaviour families
// ==========================================================================

/** Dispatch. Called once per enemy per tick from enemyAI. */
export function runBehavior(f: AiFrame): void {
  const cd = bb(f, BB_COOLDOWN);
  if (cd > 0) setBb(f, BB_COOLDOWN, cd - f.dt);
  f.w.aiTimer[f.i] += f.dt;

  if (sharedTransitions(f)) return;

  switch (f.def.family) {
    case Family.CHARGER:
      charger(f);
      break;
    case Family.ZONER:
      zoner(f);
      break;
    case Family.SHAPER:
      shaper(f);
      break;
    case Family.SWARM:
      swarm(f);
      break;
    case Family.ANCHOR:
      anchor(f);
      break;
    case Family.PARASITE:
      parasite(f);
      break;
    default:
      break;
  }
}

/**
 * Transitions every family shares: panic while burning, leash back home, drop
 * aggro when the target is gone. Returns true if it fully handled the tick.
 */
function sharedTransitions(f: AiFrame): boolean {
  const { w, i, def } = f;
  const state = w.aiState[i] as AiStateId;

  // Frozen bodies do not act. §5.1 makes them brittle instead, which is the
  // player's opening — so this is a real state, not a stun-lock inconvenience.
  //
  // THE DRAG IS A RATE, and it was a per-tick multiply until #119 measured it.
  // §5.2 converted the sibling line in `applyMaterialReactions` (`vx *= 0.6`
  // became `0.6 ** (dt * 60)`) because a drag billed per call is a drag whose
  // strength is the step size. This one is one file away, in the same status,
  // and was missed because §5.2's control-loss instrument only ever ran the
  // *player* — and the player never reaches this line. Measured on an enemy
  // holding a chase, as a fraction of the 55.9 px it covers in clean air:
  // 0.244 / 0.123 / 0.062 at a 30 / 60 / 120 Hz step, a spread of 3.94x that is
  // exactly proportional to the step, against 0.133 / 0.123 / 0.119 now.
  //
  // `updateEnemies` makes that live rather than hypothetical: it steps at most
  // 32 bodies a tick and charges the rest `stepDt = dt * stride`, so a frozen
  // enemy in a 64-body room was billed at a 30 Hz step and coasted twice as far
  // out of the freeze as the same enemy alone in a corridor. The exponent is
  // exactly 1 at the 60 Hz the shipping loop steps this pass at, so the
  // authored 0.85 does not move and neither does the number anyone has seen.
  if (hasStatus(w, i, Status.FROZEN)) {
    w.vx[i] *= Math.pow(0.85, f.dt * 60);
    return true;
  }

  // Burning organics panic. They also run *through* the room, which is how a
  // fire the player started becomes a fire the enemies spread for them.
  if (hasStatus(w, i, Status.BURNING) && state !== AiState.FLEE && f.rng.chance(0.35 * f.dt * 60)) {
    setBb(f, BB_SCRATCH, f.rng.sign());
    enter(f, AiState.FLEE);
  }

  if (state === AiState.FLEE) {
    const dir = bb(f, BB_SCRATCH) || 1;
    navigateTo(f, w.x[i] + dir * 120, w.y[i], 1.15);
    if (w.aiTimer[i] > 1.6 && !hasStatus(w, i, Status.BURNING)) enter(f, AiState.APPROACH);
    return true;
  }

  const home = i * 2;
  const hx = w.aiHome[home];
  const hy = w.aiHome[home + 1];
  const leashSq = def.leashRange * def.leashRange;
  const dxh = w.x[i] - hx;
  const dyh = w.y[i] - hy;
  if (dxh * dxh + dyh * dyh > leashSq && state !== AiState.RETURN) {
    enter(f, AiState.RETURN);
  }
  if (state === AiState.RETURN) {
    navigateTo(f, hx, hy);
    if (dxh * dxh + dyh * dyh < 900) enter(f, AiState.IDLE);
    return true;
  }

  if (f.target < 0 || f.dist > def.aggroRange * 1.6) {
    if (state !== AiState.IDLE) enter(f, AiState.IDLE);
    idle(f);
    return true;
  }

  if (state === AiState.IDLE) {
    if (f.dist <= def.aggroRange && f.los) enter(f, AiState.ALERT);
    else {
      idle(f);
      return true;
    }
  }

  if (w.aiState[i] === AiState.ALERT) {
    // A short, visible beat before committing. Enemies that snap from asleep to
    // full speed feel like they cheated.
    w.vx[i] *= 0.8;
    face(f, f.dx);
    if (w.aiTimer[i] > 0.28) enter(f, AiState.APPROACH);
    return true;
  }

  return false;
}

function idle(f: AiFrame): void {
  const { w, i } = f;
  w.vx[i] *= 0.9;
  // Anchors and shapers hold station; everything else drifts around home.
  if (f.def.family === Family.ANCHOR) return;
  if (f.rng.chance(0.01)) setBb(f, BB_SCRATCH, f.rng.sign());
  if (w.aiTimer[i] % 4 < 2) drive(f, bb(f, BB_SCRATCH) * 0.25, 0.3);
}

// ------------------------------------------------------------------ CHARGER

/**
 * Commit to a lunge, be punishable for it. The whole family exists to make the
 * dash have a job: the recover window is long on purpose.
 */
function charger(f: AiFrame): void {
  const { w, i } = f;
  switch (w.aiState[i]) {
    case AiState.APPROACH: {
      navigateTo(f, f.tx, f.ty, 1);
      if (cooldownReady(f)) {
        const a = pickAttack(f);
        if (a >= 0) beginAttack(f, a);
      }
      break;
    }
    case AiState.COMMIT: {
      // No steering during the lunge — the commitment IS the counterplay.
      if (!driveAttack(f)) enter(f, AiState.RECOVER);
      break;
    }
    case AiState.RECOVER: {
      w.vx[i] *= 0.86;
      if (w.aiTimer[i] > 0.25) enter(f, AiState.APPROACH);
      break;
    }
    default:
      enter(f, AiState.APPROACH);
      break;
  }
}

// -------------------------------------------------------------------- ZONER

/** Holds `preferredRange` and punishes you for standing still. Tests traversal. */
function zoner(f: AiFrame): void {
  const { w, i, def } = f;
  switch (w.aiState[i]) {
    case AiState.APPROACH: {
      const want = def.preferredRange;
      if (f.dist > want * 1.25) {
        navigateTo(f, f.tx, f.ty, 0.9);
      } else if (f.dist < want * 0.7) {
        // Back off along the line, still facing you.
        navigateTo(f, w.x[i] - f.dx, w.y[i] - f.dy, 1);
        face(f, f.dx);
      } else {
        if (bb(f, BB_SCRATCH) === 0) setBb(f, BB_SCRATCH, f.rng.sign());
        drive(f, bb(f, BB_SCRATCH) * 0.6, 0.6);
        face(f, f.dx);
        if (f.rng.chance(0.012)) setBb(f, BB_SCRATCH, -bb(f, BB_SCRATCH));
      }
      if (cooldownReady(f) && f.los) {
        const a = pickAttack(f);
        if (a >= 0) beginAttack(f, a);
      }
      break;
    }
    case AiState.COMMIT: {
      w.vx[i] *= 0.8;
      face(f, f.dx);
      if (!driveAttack(f)) enter(f, AiState.RECOVER);
      break;
    }
    case AiState.RECOVER: {
      setBb(f, BB_SCRATCH, f.rng.sign());
      enter(f, AiState.APPROACH);
      break;
    }
    default:
      enter(f, AiState.APPROACH);
      break;
  }
}

// ------------------------------------------------------------------- SHAPER

/**
 * The identity family. A shaper's approach logic is deliberately unhurried —
 * it wants distance and line of sight to the *room*, not to you, because its
 * attacks land on terrain. It reads the room before it acts: a shaper that
 * would flood a room that is already flooded picks its other attack instead.
 */
function shaper(f: AiFrame): void {
  const { w, i, def } = f;
  switch (w.aiState[i]) {
    case AiState.APPROACH: {
      const want = def.preferredRange;
      if (f.dist > want * 1.3) navigateTo(f, f.tx, f.ty, 0.85);
      else if (f.dist < want * 0.6) {
        navigateTo(f, w.x[i] - f.dx, w.y[i] - f.dy, 0.9);
        face(f, f.dx);
      } else {
        w.vx[i] *= 0.85;
        face(f, f.dx);
      }
      if (cooldownReady(f)) {
        const a = pickShaperAttack(f);
        if (a >= 0) beginAttack(f, a);
      }
      break;
    }
    case AiState.COMMIT: {
      // Rooted while shaping. The windup on a terrain op is long and loud
      // because the counterplay is "close the distance before it lands".
      w.vx[i] *= 0.7;
      if (!driveAttack(f)) enter(f, AiState.RECOVER);
      break;
    }
    case AiState.RECOVER: {
      // Reposition away from its own handiwork — a shaper standing in the acid
      // it just poured looks like a bug even when it is simulation-correct.
      navigateTo(f, w.x[i] - f.dx * 0.3, w.y[i], 0.8);
      if (w.aiTimer[i] > 0.5) enter(f, AiState.APPROACH);
      break;
    }
    default:
      enter(f, AiState.APPROACH);
      break;
  }
}

/**
 * Attack choice with one extra question the other families never ask: *has the
 * room already got enough of this?* Sampling a handful of cells around the
 * target and skipping a flood when the target is already submerged stops the
 * signature move from degenerating into spam.
 */
function pickShaperAttack(f: AiFrame): number {
  const { def } = f;
  let best = -1;
  let bestScore = -1;
  for (let a = 0; a < def.attacks.length; a++) {
    const atk = def.attacks[a];
    if (f.dist > atk.range) continue;
    let score = 30 + atk.cells * 0.2 + atk.damage;

    if (atk.kind === AttackKind.TERRAIN && atk.mat !== null) {
      const mat = MAT[atk.mat];
      const saturation = sampleSaturation(f, mat);
      // Halve the appeal of pouring more of what is already everywhere.
      score *= 1 - saturation * 0.85;
    }
    if (atk.op === ShaperOp.IGNITE && atk.kind === AttackKind.TERRAIN) {
      // Igniting is worth a great deal exactly when there is fuel to light.
      score *= 0.4 + sampleFlammability(f) * 2.4;
    }
    if (atk.op === ShaperOp.CHARGE && atk.kind === AttackKind.TERRAIN) {
      score *= 0.4 + sampleConductivity(f) * 2.4;
    }
    if (
      atk.kind === AttackKind.TERRAIN &&
      (atk.op === ShaperOp.ERASE || atk.op === ShaperOp.ERODE || atk.op === ShaperOp.FREEZE)
    ) {
      // Unmaking, digging and freezing all need matter to act on. Without this
      // a void pilgrim spends the fight erasing empty air, which looks exactly
      // like a broken enemy and is very nearly one.
      score *= 0.25 + sampleMatter(f) * 2.0;
    }
    if (atk.kind !== AttackKind.TERRAIN && !f.los) continue;

    score *= 0.8 + f.rng.float() * 0.4;
    if (score > bestScore) {
      bestScore = score;
      best = a;
    }
  }
  return best;
}

/** Fraction of a 9-point sample around the target already holding `mat`. */
function sampleSaturation(f: AiFrame, mat: MatId): number {
  let hits = 0;
  for (let k = 0; k < SAMPLE_OFFSETS.length; k += 2) {
    const x = Math.floor(f.tx + SAMPLE_OFFSETS[k]);
    const y = Math.floor(f.ty + SAMPLE_OFFSETS[k + 1]);
    if (f.cells.peek(x, y) === mat) hits++;
  }
  return hits / (SAMPLE_OFFSETS.length / 2);
}

function sampleFlammability(f: AiFrame): number {
  let hits = 0;
  for (let k = 0; k < SAMPLE_OFFSETS.length; k += 2) {
    const m = f.cells.peek(Math.floor(f.tx + SAMPLE_OFFSETS[k]), Math.floor(f.ty + SAMPLE_OFFSETS[k + 1]));
    if (m !== VOID && M.ignitionTemp[m] < 32000) hits++;
  }
  return hits / (SAMPLE_OFFSETS.length / 2);
}

/** Fraction of the sample that is anything at all — matter for ops to work on. */
function sampleMatter(f: AiFrame): number {
  let hits = 0;
  for (let k = 0; k < SAMPLE_OFFSETS.length; k += 2) {
    if (f.cells.peek(Math.floor(f.tx + SAMPLE_OFFSETS[k]), Math.floor(f.ty + SAMPLE_OFFSETS[k + 1])) !== VOID) {
      hits++;
    }
  }
  return hits / (SAMPLE_OFFSETS.length / 2);
}

function sampleConductivity(f: AiFrame): number {
  let hits = 0;
  for (let k = 0; k < SAMPLE_OFFSETS.length; k += 2) {
    const m = f.cells.peek(Math.floor(f.tx + SAMPLE_OFFSETS[k]), Math.floor(f.ty + SAMPLE_OFFSETS[k + 1]));
    if (m !== VOID && M.electricConductivity[m] > 0.2) hits++;
  }
  return hits / (SAMPLE_OFFSETS.length / 2);
}

/** A 9-point cross around the target. Static so sampling allocates nothing. */
const SAMPLE_OFFSETS = [0, 0, -8, 0, 8, 0, 0, -8, 0, 8, -6, 6, 6, 6, -6, -6, 6, -6];

// -------------------------------------------------------------------- SWARM

/**
 * Many weak bodies. The interesting part is not the individual — it is the
 * flocking separation, which is what turns a swarm from a single blob with
 * eight health bars into something you have to route around.
 */
function swarm(f: AiFrame): void {
  const { w, i } = f;
  switch (w.aiState[i]) {
    case AiState.APPROACH: {
      // Orbit-in rather than converge: a small perpendicular bias per member,
      // seeded from its own index so the pack spreads without communicating.
      const bias = ((i % 7) - 3) * 6;
      navigateTo(f, f.tx + bias, f.ty, 1);
      separate(f);
      if (cooldownReady(f) && f.dist < 26) {
        const a = pickAttack(f);
        if (a >= 0) beginAttack(f, a);
      }
      break;
    }
    case AiState.COMMIT: {
      if (!driveAttack(f)) enter(f, AiState.RECOVER);
      break;
    }
    case AiState.RECOVER: {
      // Peel off after a hit so the swarm pulses instead of grinding.
      navigateTo(f, w.x[i] - f.dx * 0.5, w.y[i] - 10, 1);
      if (w.aiTimer[i] > 0.35) enter(f, AiState.APPROACH);
      break;
    }
    default:
      enter(f, AiState.APPROACH);
      break;
  }
}

/** Push apart from the nearest few neighbours. Capped scan — swarms are big. */
function separate(f: AiFrame): void {
  const { w, i } = f;
  const n = w.query(Comp.ENEMY | Comp.TRANSFORM, neighbourBuf);
  let pushX = 0;
  let checked = 0;
  for (let k = 0; k < n && checked < 8; k++) {
    const j = neighbourBuf[k];
    if (j === i) continue;
    const dx = w.x[i] - w.x[j];
    const dy = w.y[i] - w.y[j];
    const d2 = dx * dx + dy * dy;
    if (d2 > 400 || d2 < 1e-4) continue;
    checked++;
    pushX += dx / d2;
  }
  if (pushX !== 0) w.vx[i] += Math.sign(pushX) * 40 * f.dt * 60;
}

const neighbourBuf = new Int32Array(256);

// ------------------------------------------------------------------- ANCHOR

/**
 * Slow, tanky, welded to its terrain. The test is your build's dig capability:
 * an anchor sitting behind granite it dug out for itself is a problem you solve
 * with a Breach module, not with your dash.
 */
function anchor(f: AiFrame): void {
  const { w, i, def } = f;
  switch (w.aiState[i]) {
    case AiState.APPROACH: {
      const home = i * 2;
      const dxh = f.tx - w.aiHome[home];
      // Anchors do not chase past their post; they lean toward you and wait.
      const reach = def.leashRange * 0.4;
      const goalX = w.aiHome[home] + Math.max(-reach, Math.min(reach, dxh));
      navigateTo(f, goalX, f.ty, 0.8);
      if (cooldownReady(f)) {
        const a = pickAttack(f);
        if (a >= 0) beginAttack(f, a);
      }
      break;
    }
    case AiState.COMMIT: {
      w.vx[i] *= 0.6;
      if (!driveAttack(f)) enter(f, AiState.RECOVER);
      break;
    }
    case AiState.RECOVER: {
      w.vx[i] *= 0.7;
      if (w.aiTimer[i] > 0.45) enter(f, AiState.APPROACH);
      break;
    }
    default:
      enter(f, AiState.APPROACH);
      break;
  }
}

// ----------------------------------------------------------------- PARASITE

/**
 * Attaches to another enemy and buffs it. Its whole design job is to make you
 * answer "which one do I kill first?" — so it actively prefers the healthiest
 * available host and flees when it has none, rather than trading with you.
 */
function parasite(f: AiFrame): void {
  const { w, i } = f;
  switch (w.aiState[i]) {
    case AiState.APPROACH: {
      let host = bb(f, BB_SCRATCH) | 0; // 1-based; 0 = none
      if (host > 0 && !hostValid(f, host - 1)) {
        host = 0;
        setBb(f, BB_SCRATCH, 0);
      }
      if (host === 0 && f.rng.chance(0.08)) {
        const found = findHost(f);
        if (found >= 0) setBb(f, BB_SCRATCH, found + 1);
        host = found + 1;
      }

      if (host > 0) {
        const j = host - 1;
        navigateTo(f, w.x[j], w.y[j] - 12, 1.1);
        const dx = w.x[i] - w.x[j];
        const dy = w.y[i] - w.y[j];
        if (dx * dx + dy * dy < 400 && cooldownReady(f)) {
          const a = pickAttack(f);
          if (a >= 0) beginAttack(f, a);
        }
      } else {
        // No host: behave like a timid charger, and back off after a bite.
        navigateTo(f, f.tx, f.ty, 1);
        if (cooldownReady(f) && f.dist < 30) {
          const a = pickAttack(f);
          if (a >= 0) beginAttack(f, a);
        }
      }
      break;
    }
    case AiState.COMMIT: {
      if (!driveAttack(f)) enter(f, AiState.RECOVER);
      break;
    }
    case AiState.RECOVER: {
      navigateTo(f, w.x[i] - f.dx * 0.6, w.y[i], 0.9);
      if (w.aiTimer[i] > 0.4) enter(f, AiState.APPROACH);
      break;
    }
    default:
      enter(f, AiState.APPROACH);
      break;
  }
}

function hostValid(f: AiFrame, j: number): boolean {
  const { w } = f;
  return j >= 0 && j < w.capacity && w.alive[j] === 1 && w.hp[j] > 0 && j !== f.i;
}

/** Highest-value living ally within reach. Throttled by the caller's chance(). */
function findHost(f: AiFrame): number {
  const { w, i } = f;
  const n = w.query(Comp.ENEMY | Comp.TRANSFORM, neighbourBuf);
  let best = -1;
  let bestScore = 0;
  for (let k = 0; k < n; k++) {
    const j = neighbourBuf[k];
    if (j === i || w.hp[j] <= 0) continue;
    if (w.team[j] !== Team.ENEMY) continue;
    if (hasStatus(w, j, Status.BUFFED)) continue;
    const dx = w.x[i] - w.x[j];
    const dy = w.y[i] - w.y[j];
    const d2 = dx * dx + dy * dy;
    if (d2 > 300 * 300) continue;
    const score = w.maxHp[j] / (1 + Math.sqrt(d2) * 0.02);
    if (score > bestScore) {
      bestScore = score;
      best = j;
    }
  }
  return best;
}

// ------------------------------------------------------------------ exports

/** Exposed for enemyAI's spawn path so blackboard conventions live in one file. */
export function initBlackboard(w: World, i: number): void {
  const b = i * 4;
  w.aiBlackboard[b + BB_COOLDOWN] = 0;
  w.aiBlackboard[b + BB_ATTACK] = -1;
  w.aiBlackboard[b + BB_SCRATCH] = 0;
  w.aiBlackboard[b + BB_PULSE] = 0;
  w.aiState[i] = AiState.IDLE;
  w.aiTimer[i] = 0;
  w.aiTarget[i] = NULL_ENTITY as Entity;
  w.aiHome[i * 2] = w.x[i];
  w.aiHome[i * 2 + 1] = w.y[i];
}
